import React, { useState, useEffect } from 'react';
import { Link, NavLink, useLocation } from 'react-router-dom';
import { useLanguage } from '../../context/LanguageContext';
import { useTheme } from '../../context/ThemeContext';
import logoSvg from '../../assets/logo.svg';
import './Navbar.css';

const SunIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <circle cx="12" cy="12" r="5"/><line x1="12" y1="1" x2="12" y2="3"/><line x1="12" y1="21" x2="12" y2="23"/>
    <line x1="4.22" y1="4.22" x2="5.64" y2="5.64"/><line x1="18.36" y1="18.36" x2="19.78" y2="19.78"/>
    <line x1="1" y1="12" x2="3" y2="12"/><line x1="21" y1="12" x2="23" y2="12"/>
    <line x1="4.22" y1="19.78" x2="5.64" y2="18.36"/><line x1="18.36" y1="5.64" x2="19.78" y2="4.22"/>
  </svg>
);

const MoonIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z"/>
  </svg>
);

const SystemIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <rect x="2" y="3" width="20" height="14" rx="2" ry="2"/><line x1="8" y1="21" x2="16" y2="21"/><line x1="12" y1="17" x2="12" y2="21"/>
  </svg>
);

const Navbar: React.FC = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const location = useLocation();
  const { language, setLanguage, t } = useLanguage();
  const { themeMode, setThemeMode } = useTheme();

  useEffect(() => {
    const handleScroll = () => setIsScrolled(window.scrollY > 20);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => { setIsMenuOpen(false); }, [location]);

  useEffect(() => {
    document.body.style.overflow = isMenuOpen ? 'hidden' : '';
    return () => { document.body.style.overflow = ''; };
  }, [isMenuOpen]);

  const navLinks = [
    { to: '/', label: t.nav.home },
    { to: '/about', label: t.nav.about },
    { to: '/contact', label: t.nav.contact },
  ];

  return (
    <header className={`navbar ${isScrolled ? 'navbar--scrolled' : ''}`}>
      <div className="navbar__container">
        <Link to="/" className="navbar__brand">
          <img
            src={logoSvg}
            alt="MahMedix"
            className="navbar__brand-logo"
          />
        </Link>

        <nav className={`navbar__nav ${isMenuOpen ? 'navbar__nav--open' : ''}`}>
          {/* Watermark logo in the drawer background */}
          <img
            src={logoSvg}
            alt=""
            aria-hidden="true"
            className="navbar__nav-watermark"
          />

          {/* Floating bubbles */}
          <div className="navbar__nav-bubbles" aria-hidden="true">
            {Array.from({ length: 12 }).map((_, i) => (
              <span
                key={i}
                className="navbar__nav-bubble"
                style={{
                  '--delay': `${(i * 0.6) % 5}s`,
                  '--x': `${(i * 23 + 8) % 90}%`,
                  '--size': `${5 + (i % 4) * 4}px`,
                } as React.CSSProperties}
              />
            ))}
          </div>

          {/* Logo shown at the top of the mobile drawer */}
          <div className="navbar__nav-logo">
            <Link to="/" onClick={() => setIsMenuOpen(false)}>
              <img src={logoSvg} alt="MahMedix" className="navbar__nav-logo-img" />
            </Link>
          </div>

          {navLinks.map((link) => (
            <NavLink
              key={link.to}
              to={link.to}
              end={link.to === '/'}
              className={({ isActive }) =>
                `navbar__link ${isActive ? 'navbar__link--active' : ''}`
              }
            >
              {link.label}
            </NavLink>
          ))}

          {/* Language toggle */}
          <div className="navbar__lang-toggle" role="group" aria-label={t.ui.language}>
            <button
              className={`navbar__lang-btn ${language === 'de' ? 'navbar__lang-btn--active' : ''}`}
              onClick={() => setLanguage('de')}
              aria-pressed={language === 'de'}
            >
              DE
            </button>
            <button
              className={`navbar__lang-btn ${language === 'en' ? 'navbar__lang-btn--active' : ''}`}
              onClick={() => setLanguage('en')}
              aria-pressed={language === 'en'}
            >
              EN
            </button>
          </div>

          {/* Theme toggle */}
          <div className="navbar__theme-toggle" role="group" aria-label={t.ui.theme}>
            <button
              className={`navbar__theme-btn ${themeMode === 'light' ? 'navbar__theme-btn--active' : ''}`}
              onClick={() => setThemeMode('light')}
              title={t.ui.lightMode}
              aria-pressed={themeMode === 'light'}
            >
              <SunIcon />
            </button>
            <button
              className={`navbar__theme-btn ${themeMode === 'system' ? 'navbar__theme-btn--active' : ''}`}
              onClick={() => setThemeMode('system')}
              title={t.ui.systemMode}
              aria-pressed={themeMode === 'system'}
            >
              <SystemIcon />
            </button>
            <button
              className={`navbar__theme-btn ${themeMode === 'dark' ? 'navbar__theme-btn--active' : ''}`}
              onClick={() => setThemeMode('dark')}
              title={t.ui.darkMode}
              aria-pressed={themeMode === 'dark'}
            >
              <MoonIcon />
            </button>
          </div>

          <Link to="/contact" className="navbar__cta-btn">
            {t.nav.getInTouch}
          </Link>
        </nav>

        <button
          className={`navbar__hamburger ${isMenuOpen ? 'navbar__hamburger--open' : ''}`}
          onClick={() => setIsMenuOpen(!isMenuOpen)}
          aria-label="Toggle menu"
          aria-expanded={isMenuOpen}
        >
          <span />
          <span />
          <span />
        </button>
      </div>

      {isMenuOpen && (
        <div className="navbar__overlay" onClick={() => setIsMenuOpen(false)} />
      )}
    </header>
  );
};

export default Navbar;
